import React from 'react'
import { FilterX, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

interface SelectionSummaryProps {
  // Active filters
  orderStatus: string[]
  setOrderStatus: (statuses: string[]) => void
  orderStatusOptions: { value: string; label: string }[]
  
  paymentStatus: string[]
  setPaymentStatus: (statuses: string[]) => void
  paymentStatusOptions: { value: string; label: string }[]
  
  clientId: string[]
  setClientId: (ids: string[]) => void
  clientOptions?: { value: string; label: string }[]
  
  issueDateFrom: string
  setIssueDateFrom: (date: string) => void
  
  issueDateTo: string
  setIssueDateTo: (date: string) => void
  
  amountFrom: string
  setAmountFrom: (amount: string) => void
  
  amountTo: string
  setAmountTo: (amount: string) => void
  
  searchQuery: string
  setSearchQuery: (searchQuery: string) => void
  
  handleClearFilters: () => void
}

export const SelectionSummary: React.FC<SelectionSummaryProps> = ({
  orderStatus,
  setOrderStatus,
  orderStatusOptions,
  paymentStatus,
  setPaymentStatus,
  paymentStatusOptions,
  clientId,
  setClientId,
  clientOptions = [],
  issueDateFrom,
  setIssueDateFrom,
  issueDateTo,
  setIssueDateTo,
  amountFrom,
  setAmountFrom,
  amountTo,
  setAmountTo,
  searchQuery,
  setSearchQuery,
  handleClearFilters,
}) => {
  // Check if any filters are active
  const hasActiveFilters =
    !orderStatus.includes('all') ||
    !paymentStatus.includes('all') ||
    !clientId.includes('all') ||
    issueDateFrom !== '' ||
    issueDateTo !== '' ||
    amountFrom !== '' ||
    amountTo !== '' ||
    searchQuery !== ''

  // Helper function to get client names from IDs
  const getClientNames = (ids: string[]) => {
    if (ids.includes('all') || ids.length === 0) return []
    
    if (!clientOptions) return []
    
    return clientOptions
      .filter(option => ids.includes(option.value))
      .map(option => option.label)
  }

  const handleSearchClear = () => {
    setSearchQuery('')
  }

  const handleClearAll = () => {
    handleClearFilters()
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {/* Search Query Badge */}
      {searchQuery && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <span>Búsqueda: "{searchQuery}"</span>
          <X 
            className="h-3 w-3 cursor-pointer hover:text-red-500" 
            onClick={handleSearchClear}
          />
        </Badge>
      )}

      {/* Order Status Filter Badge */}
      {!orderStatus.includes('all') && orderStatus.length > 0 && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <span>
            Estado orden: {orderStatusOptions
              .filter(option => orderStatus.includes(option.value))
              .map(option => option.label)
              .join(', ')
            }
          </span>
          <X 
            className="h-3 w-3 cursor-pointer hover:text-red-500" 
            onClick={() => setOrderStatus(['all'])}
          />
        </Badge>
      )}

      {/* Payment Status Filter Badge */}
      {!paymentStatus.includes('all') && paymentStatus.length > 0 && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <span>
            Estado pago: {paymentStatusOptions
              .filter(option => paymentStatus.includes(option.value))
              .map(option => option.label)
              .join(', ')
            }
          </span>
          <X 
            className="h-3 w-3 cursor-pointer hover:text-red-500" 
            onClick={() => setPaymentStatus(['all'])}
          />
        </Badge>
      )}

      {/* Client Filter Badge */}
      {!clientId.includes('all') && clientId.length > 0 && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <span>
            Cliente: {getClientNames(clientId).join(', ')}
          </span>
          <X 
            className="h-3 w-3 cursor-pointer hover:text-red-500" 
            onClick={() => setClientId(['all'])}
          />
        </Badge>
      )}

      {/* Date Range Filter Badge */}
      {(issueDateFrom || issueDateTo) && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <span>
            Fecha emisión: {issueDateFrom || '...'} - {issueDateTo || '...'}
          </span>
          <X 
            className="h-3 w-3 cursor-pointer hover:text-red-500" 
            onClick={() => {
              setIssueDateFrom('')
              setIssueDateTo('')
            }}
          />
        </Badge>
      )}

      {/* Amount Range Filter Badge */}
      {(amountFrom || amountTo) && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <span>
            Importe: ${amountFrom || '0'} - ${amountTo || '∞'}
          </span>
          <X 
            className="h-3 w-3 cursor-pointer hover:text-red-500" 
            onClick={() => {
              setAmountFrom('')
              setAmountTo('')
            }}
          />
        </Badge>
      )}

      {/* Clear All Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleClearAll}
          className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <FilterX className="h-3 w-3" />
          Limpiar todos
        </Button>
      )}
    </div>
  )
}
